import cx from 'classnames';
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import {ThemeConsumer} from '../../../themes/ThemeContext';

import styles from './Video.module.scss';

export interface VideoHandle {
    resume: () => void;
    focus: () => void;
}

/**
 * When the video element failed to run `play` as the purpose of resuming
 * a paused video element.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play#exceptions
 *
 * @param reason - Why did it fail
 */
export type FailedToResume = (reason: Error) => void;

/**
 * Resume a paused video element, and call `failedToPlay` when it failed to do
 * so
 *
 * @param video - video element
 * @param failedToPlay - a callback to be called when the play failed
 */
export const resume = (
    video: HTMLVideoElement | null,
    failedToPlay?: FailedToResume,
) => {
    if (video?.paused) {
        video.play().catch((reason: Error) => {
            failedToPlay?.(reason);
        });
    }
};

export const Video = React.forwardRef<
    VideoHandle,
    React.ComponentProps<'video'> & {
        srcObject?: MediaStream;
        sinkId?: string;
        captionsSrc?: string;
        captionsSrcLang?: string;
        autoPlay?: boolean;
        isMirrored?: boolean;
        playsInline?: boolean;
        textTrackKind?: 'captions' | 'subtitles';
        onPictureInPictureChange?: (isPip: boolean) => void;
        onFailedToResume?: FailedToResume;
        /**
         * Wraps the supplied video while retaining its props and ref. Media
         * setup follows the native element if the wrapper structure changes.
         *
         * @example
         * renderVideo={video => <div className="zoom">{video}</div>}
         */
        renderVideo?: (videoElement: React.ReactElement) => React.ReactElement;
    }
>(
    (
        {
            srcObject,
            sinkId,
            captionsSrc,
            captionsSrcLang,
            className,
            autoPlay = true,
            isMirrored = false,
            playsInline = true,
            textTrackKind,
            muted,
            onPictureInPictureChange,
            onFailedToResume,
            renderVideo,
            ...props
        },
        ref,
    ) => {
        const videoRef = useRef<HTMLVideoElement>(null);
        const [videoElement, setVideoElement] =
            useState<HTMLVideoElement | null>(null);

        /**
         * Publishes the native node for effects and synchronously for parent
         * layout effects that call the imperative handle.
         *
         * @example
         * <video ref={setVideoRef} />
         */
        const setVideoRef = useCallback((element: HTMLVideoElement | null) => {
            videoRef.current = element;
            setVideoElement(element);
        }, []);

        /**
         * Forwards browser media failures to the application's error reporter.
         *
         * @example
         * operation.catch(reportMediaFailure);
         */
        const reportMediaFailure = useCallback(
            (reason: unknown) => {
                onFailedToResume?.(
                    reason instanceof Error
                        ? reason
                        : new Error('Video operation failed', {cause: reason}),
                );
            },
            [onFailedToResume],
        );

        useImperativeHandle(ref, () => ({
            resume: () => resume(videoRef.current, onFailedToResume),
            focus: () => videoRef.current?.focus(),
        }));

        useEffect(() => {
            const player = videoElement;
            let ignore = false;
            const onEnterPip = () => onPictureInPictureChange?.(true);
            const onLeavePip = () => {
                // Video element is not paused immediately right after event of 'leavepictureinpicture'
                // That's reason why we need to schedule a timeout function
                setTimeout(() => {
                    if (ignore) {
                        return;
                    }
                    resume(player, onFailedToResume);
                }, 0);
                onPictureInPictureChange?.(false);
            };

            player?.addEventListener('enterpictureinpicture', onEnterPip);
            player?.addEventListener('leavepictureinpicture', onLeavePip);

            return () => {
                ignore = true;
                player?.removeEventListener(
                    'enterpictureinpicture',
                    onEnterPip,
                );
                player?.removeEventListener(
                    'leavepictureinpicture',
                    onLeavePip,
                );
                if (player && document.pictureInPictureElement === player) {
                    void document
                        .exitPictureInPicture?.()
                        .then(() => {
                            onPictureInPictureChange?.(false);
                        })
                        .catch(reportMediaFailure);
                }
            };
        }, [
            videoElement,
            onPictureInPictureChange,
            onFailedToResume,
            reportMediaFailure,
        ]);

        useEffect(() => {
            if (!videoElement) {
                return;
            }
            videoElement.srcObject = srcObject ?? null;
            return () => {
                videoElement.srcObject = null;
            };
        }, [videoElement, srcObject]);

        useEffect(() => {
            if (!videoElement || !srcObject?.getAudioTracks().length) {
                return;
            }
            let isCurrent = true;
            void videoElement
                .setSinkId?.(sinkId ?? '')
                .catch((reason: unknown) => {
                    if (isCurrent) {
                        reportMediaFailure(reason);
                    }
                });
            return () => {
                isCurrent = false;
            };
        }, [videoElement, sinkId, srcObject, reportMediaFailure]);

        useEffect(() => {
            const player = videoElement;
            const resumeVideo = () => resume(player, onFailedToResume);
            // Just need a track to subscribe the event
            const [track] = srcObject?.getTracks() ?? [];
            player?.addEventListener('suspend', resumeVideo);
            player?.addEventListener('pause', resumeVideo);
            track?.addEventListener('unmute', resumeVideo);
            return () => {
                player?.removeEventListener('suspend', resumeVideo);
                player?.removeEventListener('pause', resumeVideo);
                track?.removeEventListener('unmute', resumeVideo);
            };
        }, [videoElement, srcObject, onFailedToResume]);

        const muteOnCanPlay = () => {
            //FIXME: open react bug since 2017 that you cannot set muted in video element https://github.com/facebook/react/issues/10389
            if (
                videoRef.current &&
                /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
            ) {
                if (muted) {
                    videoRef.current.muted = true;
                    resume(videoRef.current, onFailedToResume);
                } else {
                    videoRef.current.muted = false;
                }
            }
        };

        return (
            <ThemeConsumer>
                {({colorScheme}) => {
                    const video = (
                        <video
                            ref={setVideoRef}
                            autoPlay={autoPlay}
                            muted={muted}
                            playsInline={playsInline}
                            className={cx(
                                styles.video,
                                colorScheme,
                                {
                                    [styles.mirrored]: isMirrored,
                                },
                                className,
                            )}
                            onCanPlay={muteOnCanPlay}
                            {...props}
                        >
                            {textTrackKind && (
                                <track
                                    kind={textTrackKind}
                                    src={captionsSrc}
                                    srcLang={captionsSrcLang}
                                    default
                                />
                            )}
                        </video>
                    );
                    return renderVideo ? renderVideo(video) : video;
                }}
            </ThemeConsumer>
        );
    },
);

Video.displayName = 'Video';
